// Supplier_Directory_Dashboard.js — Redesigned
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Paper, Slider, TextField, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiBarChart2, FiCheckCircle, FiClock, FiDollarSign, FiRefreshCw, FiSend, FiWind } from "react-icons/fi";
import { LuBuilding2 } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { MainContainer } from "../../components/container/MainContainer";
import PivotablePieChart from "../../components/chart/PivotablePieChart";
import ScatterChart from "../../components/chart/ScatterChart";
import SelectBox from "../../components/input/SelectBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Supplier_Reports } from "../../db_access/supplier_reports";
import routing from "../../routing";
import "../../style/pages-style/Engagement/engagement.css";
import MetricCard from "../../app_components/MetricCard";
import AgGrid from "../../components/grid/AgGrid";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import InvitationReport from "./InvitationReport";
import ReportDetailGrid from "./Dashboard/ReportDetailGrid";
import ReportGrid from "./Dashboard/ReportGrid";
import EngagementDashboardGlobalView from "../Engagement/EngagementDashboardGlobalView";
import { ReportTypes } from "./ReportTypes";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";

/* ─── Avatar helpers ────────────────────────────────────────────── */
const AVATAR_COLORS = ["#16a34a", "#2563eb", "#7c3aed", "#d97706", "#dc2626", "#0891b2"];
const avatarColor = (name = "") => AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
const initials = (name = "") => name.trim().split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");

/* ─── Metric card config ─────────────────────────────────────────── */
const METRIC_CONFIG = [
  { key: "supplierCount", title: "Suppliers", icon: LuBuilding2, accent: "#16a34a", bg: "rgba(22, 163, 74, 0.08)" },
  { key: "reportCount", title: "Reports Received", icon: FiCheckCircle, accent: "#2563eb", bg: "rgba(37, 99, 235, 0.08)" },
  { key: "pendingCount", title: "Pending", icon: FiClock, accent: "#d97706", bg: "rgba(217, 119, 6, 0.08)" },
  { key: "gwpReceived", title: "GWP Received", icon: FiWind, accent: "#7c3aed", bg: "rgba(124, 58, 237, 0.08)", unit: "t CO₂e" },
  { key: "costReceived", title: "Cost Received", icon: FiDollarSign, accent: "#0891b2", bg: "rgba(8, 145, 178, 0.08)" },
];

const Supplier_Directory_Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refresh, setRefresh } = useContextProvider();

  const emission = { label: "Emission", value: "Emission", company_field: "gwp", report_field: "gwp_per_unit" };
  const cost = { label: "Cost", value: "Cost", company_field: "cost", report_field: "cost_per_unit" };
  const comparisonOptions = [emission, cost];

  // ── State Hooks ─────────────────────────────────────────────
  const [globalView, setGlobalView] = useState(false);
  const [comparison, setComparison] = useState(emission.value);
  const [reports, setReports] = useState([]);
  const [acceptedReports, setAcceptedReports] = useState([]);
  const [pendingReports, setPendingReports] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [optionCompanies, setOptionCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [optionYears, setOptionYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [emissionWeight, setEmissionWeight] = useState(50);
  const [type, setType] = useState(null);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [receivedReports, setReceivedReports] = useState([]);
  const [sentReports, setSentReports] = useState([]);
  const [senders, setSenders] = useState([]);

  // Request Data State
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestDeadline, setRequestDeadline] = useState("");
  const [requestNote, setRequestNote] = useState("");

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const costWeight = 100 - emissionWeight;


  useEffect(() => { setPendingReports(reports.filter((r) => r?.status === "Pending")); }, [reports]);
  useEffect(() => {
    let temp = [...reports];
    if (selectedCompany) temp = temp.filter((r) => r?.sender_id === selectedCompany);
    if (selectedYear) temp = temp.filter((r) => r?.year === selectedYear);
    if (type) temp = temp.filter((r) => r?.type === type);
    setAcceptedReports(temp);
  }, [reports, selectedCompany, selectedYear, type]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    Supplier_Reports.get_mine_received().then((r) => r.json()).then((data) => {
      // Filter out reports sent by current user (self-sent reports)
      const filtered = data.filter(r => r.sender_id !== user?.id);
      setReports(filtered);
      setReceivedReports(filtered);
    });
    Supplier_Reports.get_mine_sent().then((r) => r.json()).then(setSentReports);
  }, [refresh]);

  useEffect(() => {
    const unique = reports.reduce((acc, r) => {
      if (!acc.some((c) => c.value === r.sender_id)) acc.push({ label: r.sender_name, value: r.sender_id });
      return acc;
    }, []);
    setOptionCompanies(unique);
  }, [reports]);

  useEffect(() => {
    setOptionYears(reports.reduce((acc, r) => {
      if (!acc.some((c) => c.value === r.year)) acc.push({ label: String(r.year), value: r.year });
      return acc;
    }, []));
  }, [reports]);

  useEffect(() => {
    const temp = new Map();
    const getScore = (r) => (emissionWeight * (r.gwp || 0) + costWeight * (r.cost || 0)) / 100;
    acceptedReports.forEach((report) => {
      const sc = getScore(report);
      if (temp.has(report.sender_id)) {
        const ex = temp.get(report.sender_id);
        ex.gwp += report.gwp || 0; ex.cost += report.cost || 0;
        ex.reports.push({ ...report, score: sc }); ex.score += sc;
      } else {
        temp.set(report.sender_id, {
          name: report.sender_name, id: report.sender_id,
          gwp: report.gwp || 0, cost: report.cost || 0, reports: [{ ...report, score: sc }], score: sc
        });
      }
    });
    let arr = Array.from(temp.values());
    const co = comparisonOptions.find((c) => c.value === comparison);
    if (co) {
      arr = arr.sort((a, b) => (a[co.company_field] > b[co.company_field] ? 1 : -1));
      arr.forEach((item) => { if (Array.isArray(item.reports)) item.reports.sort((r1, r2) => r1[co.report_field] - r2[co.report_field]); });
    }
    setCompanies(arr);
  }, [acceptedReports, comparison, emissionWeight, costWeight]);

  useEffect(() => {
    const temp = new Map();
    receivedReports.forEach((report) => {
      if (temp.has(report.sender_id)) {
        const ex = temp.get(report.sender_id);
        ex.gwp += report.gwp || 0; ex.cost += report.cost || 0; ex.reports.push(report);
      } else {
        temp.set(report.sender_id, {
          name: report.sender_name, id: report.sender_id,
          gwp: report.gwp || 0, cost: report.cost || 0, reports: [report]
        });
      }
    });
    setSenders(Array.from(temp.values()));
  }, [receivedReports]);

  /* ── computed metrics ─────────────────────────────────────────── */
  const metrics = useMemo(() => {
    // If specific companies are selected, show metrics for only those selected ones
    const activeReports = selectedCompanyIds.length > 0 
      ? receivedReports.filter(r => selectedCompanyIds.includes(r.sender_id))
      : receivedReports;
    
    const activeSenders = selectedCompanyIds.length > 0
      ? senders.filter(s => selectedCompanyIds.includes(s.id))
      : senders;

    const activePending = selectedCompanyIds.length > 0
      ? pendingReports.filter(r => selectedCompanyIds.includes(r.sender_id))
      : pendingReports;

    return {
      supplierCount: activeSenders.length,
      reportCount: activeReports.length,
      pendingCount: activePending.length,
      gwpReceived: (activeReports.reduce((s, r) => s + (r.gwp || 0), 0) / 1000).toFixed(2),
      costReceived: activeReports.reduce((s, r) => s + (r.cost || 0), 0).toFixed(2),
    };
  }, [senders, receivedReports, pendingReports, selectedCompanyIds]);

  return (
    <MainContainer>
      {/* ── Standardized High-Fidelity Metric Grid ───────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: ORG_PALETTE.spacing.metricCardGap, mb: ORG_PALETTE.spacing.pageGap }}>
        {METRIC_CONFIG.map((m, i) => (
          <MetricCard
            key={m.key}
            title={t(m.title)}
            value={metrics[m.key]}
            unit={m.unit}
            icon={m.icon}
            accent={m.accent}
            bg={m.bg}
            delay={i * 0.1}
          />
        ))}
      </Box>

      <Box sx={{ mb: 3 }}>
          {/* ── Pending Reports Banner ───────────────────────────── */}
          {pendingReports.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Typography sx={{ fontFamily: "Poppins", fontSize: 15, fontWeight: 700, color: "#111827" }}>
                  {t("Awaiting Your Review")}
                </Typography>
                <span className="eng-badge eng-badge--pending">{pendingReports.length} {t("pending")}</span>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {pendingReports.map((r, i) => (
                  <Box key={i} className="eng-invite-card" style={{ animationDelay: `${i * 0.07}s`, borderLeftColor: "#d97706" }}>
                    <Box className="eng-invite-card__header">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box className="eng-avatar" sx={{ width: 38, height: 38, fontSize: 13, background: avatarColor(r.sender_name), borderRadius: "10px" }}>
                          {initials(r.sender_name || "?")}
                        </Box>
                        <Box>
                          <Typography sx={{ fontFamily: "Poppins", fontSize: 14, fontWeight: 600, color: "#111827" }}>
                            {r.name || t("Unnamed Report")}
                          </Typography>
                          <Typography sx={{ fontFamily: "Poppins", fontSize: 12, color: "#78716c" }}>
                            {t("from")} {r.sender_name} · {r.year} · {t(r.level)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box className="eng-invite-card__footer">
                      <InvitationReport report={r} setRefresh={setRefresh} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* ── Supplier Grid Section ───────────────────────────── */}
          {companies.length === 0 ? (
            <Paper variant="outlined" sx={{ borderRadius: "16px", border: "1.5px dashed #e5e7eb" }}>
              <div className="eng-empty-state">
                <div className="eng-empty-state__title">{t("No suppliers yet")}</div>
                <div className="eng-empty-state__subtitle">
                  {t("Once a partner submits data to you and you accept it, they'll appear here")}
                </div>
                <Button
                  variant="contained"
                  endIcon={<FiArrowRight />}
                  onClick={() => navigate(routing.engagement.b2b_directory.dashboard)}
                  sx={{
                    mt: 1, backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" },
                    fontFamily: "Poppins", fontSize: 13, fontWeight: 600,
                    borderRadius: "10px", textTransform: "none",
                  }}>
                  {t("Go to B2B Directory")}
                </Button>
              </div>
            </Paper>
          ) : (
            <ReportGrid companies={companies} title={t("Suppliers")} selectedCompanyIds={selectedCompanyIds} setSelectedCompanyIds={setSelectedCompanyIds} globalView={globalView} setGlobalView={setGlobalView} />
          )}
      </Box>

      {!globalView && selectedCompanyIds.length > 0 && (
        <Box sx={{ mt: 3, animation: "eng-fadeInUp 0.4s ease both" }}>
          <ReportDetailGrid companies={companies} selectedCompanyIds={selectedCompanyIds} setSelectedCompanyIds={setSelectedCompanyIds} />
        </Box>
      )}

      {globalView && (
        <Box sx={{ animation: "eng-fadeInUp 0.4s ease both", mt: 4 }}>
          <EngagementDashboardGlobalView
            companies={companies}
            entityLabel="Suppliers"
            accentColor="#16a34a"
          />
        </Box>
      )}
      {/* ── Request Data Dialog ───────────────────────────────── */}
      <Dialog
        open={showRequestDialog}
        onClose={() => setShowRequestDialog(false)}
        PaperProps={{
          sx: { borderRadius: "18px", padding: 1, width: "100%", maxWidth: 450 }
        }}
      >
        <DialogTitle sx={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 20, pb: 1 }}>
          {t("Request Data Submission")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: "Poppins", fontSize: 14, color: "#64748b", mb: 3 }}>
            {t("Send a notification to your partners to submit their latest sustainability data.")}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box>
              <Typography sx={{ fontFamily: "Poppins", fontSize: 13, fontWeight: 700, mb: 1, color: "#1e293b" }}>
                {t("Target Suppliers")}
              </Typography>
              <Typography sx={{ fontFamily: "Poppins", fontSize: 13, color: "#64748b", p: 1.5, background: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                {selectedCompanyIds.length > 0
                  ? `${selectedCompanyIds.length} ${t("selected supplier(s)")}`
                  : t("All connected suppliers")}
              </Typography>
            </Box>

            <TextField
              label={t("Submission Deadline")}
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={requestDeadline}
              onChange={(e) => setRequestDeadline(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "12px", fontFamily: "Poppins" },
                "& .MuiInputLabel-root": { fontFamily: "Poppins", fontWeight: 600 }
              }}
            />

            <TextField
              label={t("Additional Instructions")}
              placeholder={t("e.g. Please provide Scope 3 data for Q4 2025...")}
              multiline
              rows={3}
              fullWidth
              value={requestNote}
              onChange={(e) => setRequestNote(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "12px", fontFamily: "Poppins" },
                "& .MuiInputLabel-root": { fontFamily: "Poppins", fontWeight: 600 }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setShowRequestDialog(false)}
            sx={{ fontFamily: "Poppins", textTransform: "none", fontWeight: 600, color: "#64748b" }}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowRequestDialog(false);
              alert(t("Data request and deadline notification sent successfully."));
            }}
            disabled={!requestDeadline}
            sx={{
              fontFamily: "Poppins", textTransform: "none", fontWeight: 600,
              background: "#16a34a", "&:hover": { background: "#15803d" },
              borderRadius: "10px", px: 4, py: 1
            }}
          >
            {t("Send Request")}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Supplier_Directory_Dashboard;

